import { BUTTON_VARIANTS, type ButtonVariant } from "../../atoms/button/button.shared";
import { analyticsText, quizQuestionDimValue, UNTITLED_LABEL } from "../../services/analytics";
import { getMultiOfferDuration } from "./utils/offer-duration";
import type { Answers } from "./engine/engine.types";
import type { ResolvedPriceTier } from "./product-recommender.types";

/* Button's whole set, as on center-banner, so every organism shares one CTA
   option list. ⚠ `ai` sits in no frame here — offered, not designed for. */
export const CTA_VARIANTS = BUTTON_VARIANTS;

/** An authored CTA treatment from CTA_VARIANTS, or `fallback` for anything else. */
export const ctaVariant = (asked: unknown, fallback: ButtonVariant): ButtonVariant => (
  typeof asked === "string" && Object.prototype.hasOwnProperty.call(CTA_VARIANTS, asked) ?
    (asked as ButtonVariant) :
    fallback
);

/** The quiz's tool id: `quiz_status`'s dim_value and the root of every link position. */
export const QUIZ_NAME = "product recommendation quiz";
/** `select_item`'s list id and name. */
export const ITEM_LIST_NAME = "ai product recommender";
const QUIZ_VIRTUAL_PATH = "product_recommendation_quiz";
const MODAL_PREFIX = "modal_";

/** The entry button's position, and the root the others compose from. */
export const quizLinkPosition = (isModal: boolean): string => `${isModal ? MODAL_PREFIX : ""}${QUIZ_NAME}`;

/** An answer option's position: the root, then "2 of 4 - question". */
export const optionLinkPosition = (isModal: boolean, progressLabel: string, questionLabel?: string): string => `${quizLinkPosition(isModal)} - ${quizQuestionDimValue(progressLabel, questionLabel)}`;

/** Next, Back and Close: the root and the question, deliberately without the progress token. */
export const navLinkPosition = (isModal: boolean, questionLabel?: string): string => `${quizLinkPosition(isModal)} - ${analyticsText(questionLabel) || UNTITLED_LABEL}`;

/** Every control on the results screen. */
export const recommendationLinkPosition = (isModal: boolean): string => `${quizLinkPosition(isModal)} recommendation`;

/** The host page, query dropped and trailing slash collapsed, then the quiz's own path. */
export const quizVirtualUrl = (): string => {
  if (typeof window === "undefined") {
    return "";
  }
  const { origin, pathname } = window.location;
  return `${origin}${(pathname || "").replace(/\/+$/, "")}/${QUIZ_VIRTUAL_PATH}`;
};

export const currentPageUrl = (): string => (typeof window === "undefined" ? "" : window.location.href);

/** A shop destination made absolute for reporting only — the href keeps the authored value. */
export const absoluteCtaUrl = (url?: string | null): string => {
  if (!url) {
    return "";
  }
  if (typeof window === "undefined") {
    return url;
  }
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
};

/** A Signature card's reported id, e.g. "choice-package". */
export const broadItemId = (slug?: string | null): string => (slug ? `${analyticsText(slug)}-package` : "");

/** A genre-pack card's reported id: its shop codes, lower case, pipe-joined. */
export const focusedItemId = (shopCodes: (string | null | undefined)[]): string => (shopCodes || []).map(code => analyticsText(code)).filter(Boolean).join("|");

const SITE_BASE = "https://www.directv.com/uf/";
const SCHEME_WITHOUT_AUTHORITY = /^[a-z][a-z\d+.-]*:(?!\/\/)/i;
const AUTHORED_ABSOLUTE = /^https:\/\//i;

const isHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value, "https://www.directv.com");
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
};

const staysOnSite = (url: string): boolean => {
  if (SCHEME_WITHOUT_AUTHORITY.test(url)) {
    return false;
  }
  const base = new URL(SITE_BASE);
  try {
    return new URL(url, base).origin === base.origin || AUTHORED_ABSOLUTE.test(url);
  } catch {
    return false;
  }
};

/** A results button's destination, or null when it must not render: blank, not
    http(s), off-site without an explicit https://, or relative to the page. */
export const safeCtaUrl = (authored: unknown): string | null => {
  const trimmed = String(authored ?? "").trim();
  if (!/[^\p{Cc}\p{Cf}\s]/u.test(trimmed) || !isHttpUrl(trimmed) || !staysOnSite(trimmed)) {
    return null;
  }
  return /^https?:\/\//i.test(trimmed) || /^\/(?!\/)/.test(trimmed) ? trimmed : null;
};

/** "$59.99/mo." — the struck regular price. */
export const fmtPrice = (n: number): string => `$${n.toFixed(2)}/mo.`;

export const splitPrice = (n: number): { dollars: string; cents: string } => {
  const [dollars, cents] = n.toFixed(2).split(".");
  return {
    dollars: `$${dollars}`,
    cents: `.${cents}`
  };
};

export interface PriceCopy {
  promo?: string;
  ongoing?: string;
  nextTier?: string;
}

const PRICE_COPY_DEFAULTS = {
  promo: "/mo. for [promo_window]",
  ongoing: "/mo. + tax",
  nextTier: "then [next_tier_price]/mo. for [next_tier_window]"
};

const UNFILLED_TOKEN = /\[[a-z0-9_]+\]/i;

const substitute = (text: string, values: Record<string, string>): string => Object.keys(values).reduce((out, key) => out.split(`[${key}]`).join(values[key]), text);

/* Both gates are needed: a template missing a required token would state a
   promo price with no window, and one carrying a token nobody fills would
   print the bracket to the shopper. Either falls back to the built-in wording. */
const fillTemplate = (template: string | undefined, fallback: string, values: Record<string, string>): string => {
  const hasAllTokens = typeof template === "string" && !!template.trim() &&
    Object.keys(values).every(key => template.includes(`[${key}]`));
  const authored = hasAllTokens ? substitute(template, values) : null;
  return authored !== null && !UNFILLED_TOKEN.test(authored) ? authored : substitute(fallback, values);
};

/** What follows the cents: a known promo states its window, a known ongoing
    rate says "+ tax", and an unknown one claims nothing, so a promo price is
    never advertised as permanent. */
export const priceSuffix = (
  promoMonths: number | null | undefined,
  hasNextTier: boolean,
  promoKnown: boolean,
  copy: PriceCopy
): string => {
  if (promoMonths == null || promoMonths <= 0) {
    return promoKnown ? fillTemplate(copy.ongoing, PRICE_COPY_DEFAULTS.ongoing, {}) : "/mo.";
  }
  const promoWindow = promoMonths === 1 ? "1st mo" : `first ${promoMonths} mos`;
  const resolved = fillTemplate(copy.promo, PRICE_COPY_DEFAULTS.promo, { promo_window: promoWindow });
  return hasNextTier && !resolved.trim().endsWith(",") ? `${resolved},` : resolved;
};

/** "then $84.99/mo. for mos 2-24", or null. Re-checked, because a hand-built
    catalog can carry a string price or a window that skips months. */
export const nextTierLine = (
  tier: ResolvedPriceTier | null | undefined,
  promoMonths: number | null | undefined,
  copy: PriceCopy
): string | null => {
  if (!tier || promoMonths == null || promoMonths <= 0 || !Number.isFinite(tier.price)) {
    return null;
  }
  if (!Number.isInteger(tier.startMo) || !Number.isInteger(tier.endMo) ||
    tier.startMo !== promoMonths + 1 || tier.endMo < tier.startMo) {
    return null;
  }
  const span = getMultiOfferDuration(tier.startMo, tier.endMo);
  return fillTemplate(copy.nextTier, PRICE_COPY_DEFAULTS.nextTier, {
    next_tier_price: `$${tier.price.toFixed(2)}`,
    next_tier_window: tier.startMo === tier.endMo ? span : `mos ${span}`
  });
};

/** Only a completed run is written, so `phase` is "results" on every row this writes. */
export interface StoredSession {
  answers?: Answers;
  phase?: "ask" | "results";
}

/** Every remembered run is stored under this prefix and the block id. */
export const STORAGE_KEY_PREFIX = "dds-product-recommender:";

export const storageKey = (id?: string): string => `${STORAGE_KEY_PREFIX}${id || "default"}`;

/* Resume is best-effort: private mode and a full quota both throw here. */
export const readSession = (key: string): StoredSession | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const writeSession = (key: string, value: StoredSession): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable: the run simply is not resumed.
  }
};

export const clearSession = (key: string): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable: there is nothing to clear.
  }
};

/** Whether a stored row is a completed run worth replaying. Any same-origin
    script can write sessionStorage, so the answers are checked as closely as the phase. */
export const isRestorableSession = (saved: StoredSession | null): saved is { answers: Answers; phase: "results" } => {
  const answers = saved?.answers;
  return saved?.phase === "results" &&
    !!answers && typeof answers === "object" && !Array.isArray(answers) &&
    Object.keys(answers).length > 0 &&
    Object.values(answers).every(value => Array.isArray(value) && value.every(item => typeof item === "string"));
};
